/* eslint-disable @typescript-eslint/no-explicit-any, no-empty */
import { useState, useEffect, useCallback } from "react";
import AdBannerSlot from "@/components/AdBannerSlot";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { formatCityWithDepartment, formatTitleWithDepartment } from "@/lib/cityDepartment";
import { PROFILE_PUBLIC_COLUMNS } from "@/lib/profileColumns";
import { useAuth } from "@/hooks/useAuth";
import { useProAccess } from "@/hooks/useProAccess";
import { canViewParentAnnouncements } from "@/lib/proAccessRules";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/use-toast";
import ProfileQuickView from "@/components/ProfileQuickView";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, MapPin, Star, Clock, Filter, User, Calendar, Heart, Home, ShieldCheck, TreePine, Navigation, Loader2, Zap, Lock, Crown } from "lucide-react";
import SearchAutocomplete, { type GeoSuggestion } from "@/components/SearchAutocomplete";
import GeoConsentDialog from "@/components/GeoConsentDialog";
import { useGeolocation } from "@/hooks/useGeolocation";
import { PRO_SERVICES } from "@/lib/proAvailability";
import type { Database } from "@/integrations/supabase/types";
import Seo from "@/components/Seo";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type AvailabilitySlot = Database["public"]["Tables"]["availability_slots"]["Row"];

interface ParentRequest {
  id: string;
  parent_id: string;
  title: string;
  description: string | null;
  city: string;
  care_type: string | null;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  proposed_rate: number | null;
  care_at_pro_home: boolean | null;
  care_at_parent_home: boolean | null;
  care_outdoor: boolean | null;
  non_smoker: boolean | null;
  has_car: boolean | null;
  first_aid_required: boolean | null;
  pets: boolean | null;
  languages: string | null;
  additional_tasks: string | null;
}

const CARE_TYPE_LABELS: Record<string, string> = {
  ponctuelle: "Ponctuelle",
  reguliere: "Régulière",
  urgente: "Urgente",
  periscolaire: "Périscolaire",
  nuit: "Garde de nuit",
  week_end: "Week-end",
};

// Haversine distance in km
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const RADIUS_KM = 40;

const SearchPage = () => {
  const { user, role, subscriptionActive, loading: authLoading } = useAuth();
  const proAccess = useProAccess();
  const { favoriteIds, isFavorite, toggle: toggleFavorite } = useFavorites();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [filtered, setFiltered] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [reviewStats, setReviewStats] = useState<Record<string, { avg: number; count: number }>>({});
  const [requests, setRequests] = useState<ParentRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const proAnnouncementAccessLoading =
    authLoading || Boolean(user && role === null) || (role === "pro" && proAccess.status === "loading");
  const proCanViewAnnouncements = canViewParentAnnouncements(role, subscriptionActive);
  const defaultTab = role === "pro" && !proCanViewAnnouncements ? "pros" : role === "pro" ? "parents" : "pros";

  // Filters
  const [search, setSearch] = useState("");
  const [selectedGeo, setSelectedGeo] = useState<GeoSuggestion | null>(null);
  const [slots, setSlots] = useState<Record<string, AvailabilitySlot[]>>({});
  const [maxRate, setMaxRate] = useState([100]);
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");
  const [instantOnly, setInstantOnly] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>("name");
  const [paymentFilters, setPaymentFilters] = useState<Array<"stripe" | "cash" | "paypal">>([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);


  const toggleService = useCallback((column: string) => {
    setSelectedServices((prev) =>
      prev.includes(column) ? prev.filter((s) => s !== column) : [...prev, column],
    );
  }, []);

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    // If user clears or modifies the text, reset geo selection
    if (!val.trim()) setSelectedGeo(null);
  }, []);

  const handleGeoSelect = useCallback((geo: GeoSuggestion) => {
    setSelectedGeo(geo);
  }, []);

  // ─── Géolocalisation utilisateur (RGPD : ~500 m) ───
  const { consent: geoConsent, coords: userCoords, loading: geoLoading } = useGeolocation();
  const [geoDialogOpen, setGeoDialogOpen] = useState(false);

  const applyUserCoords = (lat: number, lng: number) => {
    setSelectedGeo({ name: "Autour de moi", lat, lng } as GeoSuggestion);
    setSearch("Autour de moi");
    setSortBy("distance");
  };

  const handleNearMe = () => {
    if (geoConsent === "granted" && userCoords) {
      applyUserCoords(userCoords.latitude, userCoords.longitude);
      return;
    }
    setGeoDialogOpen(true);
  };

  const handleGeoGranted = () => {
    // Les coords fraîches sont déjà persistées par le dialog ; on les relit.
    try {
      const raw = localStorage.getItem("cg_geo_coords_v1");
      if (raw) {
        const c = JSON.parse(raw) as { latitude: number; longitude: number };
        applyUserCoords(c.latitude, c.longitude);
      }
    } catch {}
  };

  useEffect(() => {
    const fetchPros = async () => {

      // Fetch all pro profiles (those with a role of 'pro')
      const { data: proRoles } = await supabase.from("user_roles").select("user_id").eq("role", "pro");
      const proUserIds = (proRoles ?? []).map((r) => r.user_id);

      if (proUserIds.length === 0) {
        setProfiles([]);
        setLoading(false);
        return;
      }

      const { data } = await supabase.from("profiles").select(PROFILE_PUBLIC_COLUMNS as any).in("user_id", proUserIds).returns<any[]>();

      // Tous les comptes PRO restent visibles aux parents, même si leur
      // abonnement, identité ou profil ne sont pas encore finalisés.
      // Seule une suspension administrative actuellement active masque le profil.
      const prosList = (data ?? []).filter((p) => {
        const activelySuspended =
          Boolean(p.suspended_at) &&
          (!p.suspended_until || new Date(p.suspended_until).getTime() > Date.now());
        return !activelySuspended;
      });
      setProfiles(prosList);

      // Fetch all availability slots for these pros
      const proIds = prosList.map((p) => p.user_id);
      if (proIds.length > 0) {
        const { data: slotsData } = await supabase
          .from("availability_slots")
          .select("*")
          .in("pro_id", proIds);

        const grouped: Record<string, AvailabilitySlot[]> = {};
        (slotsData ?? []).forEach((s) => {
          if (!grouped[s.pro_id]) grouped[s.pro_id] = [];
          grouped[s.pro_id].push(s);
        });
        setSlots(grouped);
      }

      // Fetch review stats for all pros
      if (proIds.length > 0) {
        const { data: reviewsData } = await supabase
          .from("reviews")
          .select("pro_id, rating")
          .in("pro_id", proIds);

        const stats: Record<string, { avg: number; count: number }> = {};
        (reviewsData ?? []).forEach((r) => {
          if (!stats[r.pro_id]) stats[r.pro_id] = { avg: 0, count: 0 };
          stats[r.pro_id].count += 1;
          stats[r.pro_id].avg += r.rating;
        });
        Object.keys(stats).forEach((k) => {
          stats[k].avg = Math.round((stats[k].avg / stats[k].count) * 10) / 10;
        });
        setReviewStats(stats);
      }

      setLoading(false);
    };
    fetchPros();

    const channel = supabase
      .channel("profiles-availability")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles" },
        (payload) => {
          const updated = payload.new as Profile;
          const activelySuspended =
            Boolean(updated.suspended_at) &&
            (!updated.suspended_until || new Date(updated.suspended_until).getTime() > Date.now());
          setProfiles((prev) => {
            if (activelySuspended) return prev.filter((p) => p.id !== updated.id);
            if (prev.some((p) => p.id === updated.id)) {
              return prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p));
            }
            return [...prev, updated];
          });
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "profiles" },
        async (payload) => {
          const newProfile = payload.new as Profile;
          // Check if this new profile is a pro
          const { data: roles } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", newProfile.user_id)
            .eq("role", "pro");
          if (roles && roles.length > 0) {
            setProfiles((prev) => {
              if (prev.some((p) => p.id === newProfile.id)) return prev;
              return [...prev, newProfile];
            });
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (!user?.id || proAnnouncementAccessLoading || !proCanViewAnnouncements) {
      setRequests([]);
      setRequestsLoading(proAnnouncementAccessLoading);
      return;
    }
    let cancelled = false;
    const fetchRequests = async () => {
      setRequestsLoading(true);
      const { data } = await supabase.rpc("list_open_care_requests");
      if (!cancelled) {
        setRequests((data ?? []) as ParentRequest[]);
        setRequestsLoading(false);
      }
    };
    fetchRequests();
    const channel = supabase
      .channel(`search-care-requests-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "care_requests" }, fetchRequests)
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user?.id, proAnnouncementAccessLoading, proCanViewAnnouncements]);

  useEffect(() => {
    let result = [...profiles];

    if (selectedGeo) {
      // Geo-based filter: city/postal match OR within radius
      result = result.filter((p) => {
        const geoName = selectedGeo.name.toLowerCase().trim();
        const cityMatch = p.city?.toLowerCase().trim() === geoName;
        const postalMatch =
          selectedGeo.postcode && p.postal_code
            ? p.postal_code.startsWith(String(selectedGeo.postcode).slice(0, 2))
            : false;
        if (cityMatch || postalMatch) return true;
        const lat = (p as any).latitude;
        const lng = (p as any).longitude;
        if (lat == null || lng == null) return false;
        return haversineKm(selectedGeo.lat, selectedGeo.lng, lat, lng) <= RADIUS_KM;
      });
    } else if (search.trim()) {
      // Text-based fallback filter
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.city?.toLowerCase().includes(q) ||
          p.postal_code?.toLowerCase().includes(q) ||
          (p.full_name ?? "").toLowerCase().includes(q)
      );
    }

    // Max hourly rate
    result = result.filter((p) => !p.hourly_rate || p.hourly_rate <= maxRate[0]);

    // Availability filter
    if (availabilityFilter !== "all") {
      const now = new Date();
      const currentDay = now.getDay(); // 0=Sun
      const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

      const hasSlotInRange = (proId: string, dayRange: number[], timeStart?: string, timeEnd?: string) => {
        const proSlots = slots[proId] ?? [];
        return proSlots.some((s) => {
          if (s.is_blocked) return false;
          if (!dayRange.includes(s.day_of_week)) return false;
          if (timeStart && s.end_time <= timeStart) return false;
          if (timeEnd && s.start_time >= timeEnd) return false;
          return true;
        });
      };

      result = result.filter((p) => {
        switch (availabilityFilter) {
          case "available":
            return p.is_available;
          case "within_hour": {
            // Available today, with a slot covering the current hour
            const oneHourLater = `${String(Math.min(now.getHours() + 1, 23)).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
            return p.is_available && hasSlotInRange(p.user_id, [currentDay], currentTime, oneHourLater);
          }
          case "within_3days": {
            const days = Array.from({ length: 3 }, (_, i) => (currentDay + i) % 7);
            return hasSlotInRange(p.user_id, days);
          }
          case "within_7days": {
            const days = Array.from({ length: 7 }, (_, i) => (currentDay + i) % 7);
            return hasSlotInRange(p.user_id, days);
          }
          case "night": {
            // Night: 20:00 - 06:00
            const allDays = [0, 1, 2, 3, 4, 5, 6];
            return hasSlotInRange(p.user_id, allDays, "20:00") || hasSlotInRange(p.user_id, allDays, undefined, "06:00");
          }
          case "evening": {
            // Evening: 17:00 - 22:00
            const allDays = [0, 1, 2, 3, 4, 5, 6];
            return hasSlotInRange(p.user_id, allDays, "17:00", "22:00");
          }
          case "morning": {
            // Morning: 06:00 - 12:00
            const allDays = [0, 1, 2, 3, 4, 5, 6];
            return hasSlotInRange(p.user_id, allDays, "06:00", "12:00");
          }
          default:
            return true;
        }
      });
    }

    // Filtre "dispo dans l'heure" — basé sur la colonne instant_available + horodatage valide
    if (instantOnly) {
      const nowIso = Date.now();
      result = result.filter((p) => {
        const ia = (p as any).instant_available as boolean | null;
        const until = (p as any).instant_available_until as string | null;
        return !!ia && !!until && new Date(until).getTime() > nowIso;
      });
    }

    // Filtres services (le pro doit proposer TOUS les services cochés)
    if (selectedServices.length > 0) {
      result = result.filter((p) =>
        selectedServices.every((col) => Boolean((p as any)[col])),
      );
    }

    // Filtres modes de paiement (le pro doit accepter AU MOINS UN des modes cochés)
    if (paymentFilters.length > 0) {
      result = result.filter((p) =>
        paymentFilters.some((m) => {
          if (m === "stripe") return (p as any).accepts_stripe !== false;
          if (m === "cash") return !!(p as any).accepts_cash;
          if (m === "paypal") return !!(p as any).accepts_paypal;
          return false;
        }),
      );
    }


    // Accès instantané aux profils enregistrés par le parent.
    if (favoritesOnly && role === "parent") {
      result = result.filter((p) => favoriteIds.has(p.user_id));
    }

    // Sort
    if (sortBy === "price_asc") {
      result.sort((a, b) => (a.hourly_rate ?? 0) - (b.hourly_rate ?? 0));
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => (b.hourly_rate ?? 0) - (a.hourly_rate ?? 0));
    } else if (sortBy === "distance" && selectedGeo) {
      result.sort((a, b) => {
        const dA = haversineKm(selectedGeo.lat, selectedGeo.lng, (a as any).latitude ?? 0, (a as any).longitude ?? 0);
        const dB = haversineKm(selectedGeo.lat, selectedGeo.lng, (b as any).latitude ?? 0, (b as any).longitude ?? 0);
        return dA - dB;
      });
    } else {
      result.sort((a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? ""));
    }

    setFiltered(result);
  }, [profiles, search, selectedGeo, maxRate, availabilityFilter, instantOnly, selectedServices, sortBy, slots, paymentFilters, favoritesOnly, favoriteIds, role]);

  const handleFavoriteToggle = async (proId: string) => {
    const wasFavorite = isFavorite(proId);
    try {
      const nowFavorite = await toggleFavorite(proId);
      toast({
        title: nowFavorite ? "Ajouté aux favoris ❤️" : "Retiré des favoris",
        description: nowFavorite
          ? "Vous retrouverez ce professionnel dans Mes favoris."
          : "Ce professionnel a été retiré de vos favoris.",
      });
    } catch (error) {
      console.error("Favorite toggle failed:", error);
      toast({
        title: "Favori non enregistré",
        description: wasFavorite ? "Impossible de retirer ce favori pour le moment." : "Impossible d'ajouter ce favori pour le moment.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Baby-sitter & nounou près de chez vous"
        description="Trouvez une baby-sitter, nounou ou assistante maternelle près de chez vous. Consultez les profils inscrits et contactez-les directement sur Click&Gard."
        path="/search"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Annuaire des baby-sitters et nounous Click&Gard",
            description:
              "Annuaire des professionnels de la garde d'enfants inscrits sur Click&Gard en France : baby-sitters, nounous et assistantes maternelles.",
            url: "https://clickandgard.fr/search",
            inLanguage: "fr-FR",
            isPartOf: { "@type": "WebSite", name: "Click&Gard", url: "https://clickandgard.fr" },
            potentialAction: {
              "@type": "SearchAction",
              target: "https://clickandgard.fr/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://clickandgard.fr/" },
              { "@type": "ListItem", position: 2, name: "Rechercher", item: "https://clickandgard.fr/search" },
            ],
          },
        ]}
      />
      <Navbar />
      <div className="container mx-auto px-4 max-w-5xl"><AdBannerSlot pageKey="search" /></div>

      {/* Hero search */}
      <section className="relative py-12 page-enter overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-background to-secondary/5" />
        <div className="absolute top-[-100px] right-[-50px] w-[300px] h-[300px] rounded-full bg-primary/5 blur-[100px] pointer-events-none" />
        <div className="relative w-full px-4 md:px-6 lg:px-8 max-w-2xl mx-auto text-center">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Trouvez votre <span className="text-gradient-neon">garde d'enfants</span>
          </h1>
          <p className="text-muted-foreground mb-6">
            Recherchez par ville ou code postal
          </p>
          <div className="flex gap-2">
            <SearchAutocomplete value={search} onChange={handleSearchChange} onSelect={handleGeoSelect} />
            <Button size="lg" className="rounded-full h-12 px-6 bg-gradient-to-r from-primary to-secondary shadow-neon hover:opacity-90">
              <Search className="w-5 h-5" />
            </Button>
          </div>
          <div className="mt-3 flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleNearMe}
              disabled={geoLoading}
              className="rounded-full h-9 px-4 gap-2 border-primary/30 hover:border-primary hover:bg-primary/5"
            >
              {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4 text-primary" />}
              Autour de moi
              {geoConsent === "granted" && <span className="text-[10px] text-muted-foreground">(±500&nbsp;m)</span>}
            </Button>
          </div>
        </div>
      </section>

      <GeoConsentDialog
        open={geoDialogOpen}
        onOpenChange={setGeoDialogOpen}
        onGranted={handleGeoGranted}
      />

      <div className="w-full px-4 md:px-6 lg:px-8 py-2">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-2 rounded-full">
            <TabsTrigger value="pros" className="rounded-full">👶 Professionnels Click&Gard</TabsTrigger>
            <TabsTrigger value="parents" className="rounded-full">
              {role === "pro" && !proCanViewAnnouncements ? "🔒 Parents / Annonces" : "❤️ Parents / Annonces"}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pros" className="mt-0">
      {!search.trim() && !selectedGeo && !loading && profiles.length > 0 && (
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <h2 className="font-display text-xl font-bold text-foreground mb-1">
            Professionnels Click&Gard
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Découvrez les professionnels inscrits sur Click&Gard, y compris ceux dont l'activation est encore en cours.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(() => {
              // Pick up to 8 profiles from different cities
              const seen = new Set<string>();
              const suggestions: Profile[] = [];
              for (const p of profiles) {
                const city = p.city?.toLowerCase() ?? "";
                if (!seen.has(city) || suggestions.length < 4) {
                  seen.add(city);
                  suggestions.push(p);
                  if (suggestions.length >= 8) break;
                }
              }
              return suggestions.map((pro) => (
                <Card
                  key={pro.id}
                  className="card-neon shadow-neon hover:shadow-lg transition-all overflow-hidden cursor-pointer relative group"
                  onClick={() => { setSelectedProfile(pro); setQuickViewOpen(true); }}
                >
                  {role === "parent" && (
                    <button
                      type="button"
                      className={`absolute top-2.5 right-2.5 z-10 w-10 h-10 rounded-full border shadow-sm backdrop-blur flex items-center justify-center transition-all hover:scale-105 ${isFavorite(pro.user_id) ? "bg-destructive/10 border-destructive/30" : "bg-background/90 border-border hover:border-destructive/30"}`}
                      onClick={(e) => { e.stopPropagation(); void handleFavoriteToggle(pro.user_id); }}
                      aria-label={isFavorite(pro.user_id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                      title={isFavorite(pro.user_id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                    >
                      <Heart className={`w-5 h-5 transition-colors ${isFavorite(pro.user_id) ? "text-destructive fill-destructive" : "text-muted-foreground hover:text-destructive"}`} />
                    </button>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        {pro.avatar_url ? (
                          <img src={pro.avatar_url} alt={pro.full_name || "Professionnel Click&Gard"} className="w-12 h-12 rounded-xl object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-foreground truncate flex items-center gap-1">
                          {pro.full_name || "Professionnel Click&Gard"}
                          {pro.identity_verified && (
                            <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" />
                          )}
                        </h3>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {pro.city || "Localisation à compléter"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="rounded-full text-xs">
                        {pro.hourly_rate ? `${pro.hourly_rate} €/h` : "Tarif à compléter"}
                      </Badge>
                      <Badge className={`rounded-full border text-xs ${pro.is_available ? "availability-badge-available" : "availability-badge-unavailable"}`}>
                        {pro.is_available ? "Disponible" : "Indisponible"}
                      </Badge>
                      {reviewStats[pro.user_id] && (
                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-primary fill-primary" />
                          {reviewStats[pro.user_id].avg}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap mt-2">
                      {((pro as any).accepts_stripe ?? true) && (
                        <span title="Paiement sécurisé Click&Gard" className="text-sm">💳</span>
                      )}
                      {(pro as any).accepts_cash && (
                        <span title="Paiement en espèces" className="text-sm">💶</span>
                      )}
                      {(pro as any).accepts_paypal && (
                        <span title="Paiement PayPal" className="text-sm">🅿️</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ));
            })()}
          </div>
        </div>
      )}


      <div className="w-full px-4 md:px-6 lg:px-8 py-8">
        {/* Filter bar */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-muted-foreground">
              {filtered.length} professionnel{filtered.length !== 1 ? "s" : ""} trouvé{filtered.length !== 1 ? "s" : ""}
            </p>
            {(selectedServices.length > 0 || instantOnly || availabilityFilter !== "all") && (
              <Badge variant="secondary" className="rounded-full text-xs">
                {selectedServices.length + (instantOnly ? 1 : 0) + (availabilityFilter !== "all" ? 1 : 0)} filtre{(selectedServices.length + (instantOnly ? 1 : 0) + (availabilityFilter !== "all" ? 1 : 0)) > 1 ? "s" : ""} actif{(selectedServices.length + (instantOnly ? 1 : 0) + (availabilityFilter !== "all" ? 1 : 0)) > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {role === "parent" && (
            <Button
              type="button"
              variant={favoritesOnly ? "default" : "outline"}
              size="sm"
              className="rounded-full gap-2"
              onClick={() => setFavoritesOnly((value) => !value)}
            >
              <Heart className={`w-4 h-4 ${favoritesOnly ? "fill-current" : ""}`} />
              Mes favoris
              <span className={`min-w-5 h-5 px-1.5 rounded-full text-[11px] flex items-center justify-center ${favoritesOnly ? "bg-background/20" : "bg-muted"}`}>
                {favoriteIds.size}
              </span>
            </Button>
          )}
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            className="rounded-full"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-1" /> {showFilters ? "Masquer les filtres" : "Afficher les filtres"}
          </Button>
        </div>

        {showFilters && (
          <Card className="mb-6 card-neon shadow-neon">
            <CardContent className="p-6 space-y-6">
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Tarif max : {maxRate[0]} €/h</Label>
                  <Slider value={maxRate} onValueChange={setMaxRate} max={100} min={5} step={5} />
                </div>
                <div className="space-y-2">
                  <Label>Disponibilité</Label>
                  <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="available">Disponibles uniquement</SelectItem>
                      <SelectItem value="within_hour">Disponible dans l'heure</SelectItem>
                      <SelectItem value="within_3days">Disponible dans les 3 jours</SelectItem>
                      <SelectItem value="within_7days">Disponible dans les 7 jours</SelectItem>
                      <SelectItem value="night">Disponible pour la nuit</SelectItem>
                      <SelectItem value="evening">Disponible en soirée</SelectItem>
                      <SelectItem value="morning">Disponible le matin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Trier par</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Nom</SelectItem>
                      <SelectItem value="price_asc">Tarif croissant</SelectItem>
                      <SelectItem value="price_desc">Tarif décroissant</SelectItem>
                      {selectedGeo && <SelectItem value="distance">Distance</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Toggle Dispo dans l'heure */}
              <div>
                <button
                  type="button"
                  onClick={() => setInstantOnly(!instantOnly)}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                    instantOnly
                      ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300 shadow-sm"
                      : "border-border bg-muted/30 text-muted-foreground hover:border-amber-500/40"
                  }`}
                >
                  <Zap className={`w-4 h-4 ${instantOnly ? "text-amber-500" : ""}`} />
                  Dispo dans l'heure uniquement
                </button>
              </div>

              {/* Filtres services */}
              <div className="space-y-2">
                <Label>Services proposés</Label>
                <p className="text-xs text-muted-foreground">Le pro doit proposer tous les services cochés.</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {PRO_SERVICES.map((s) => {
                    const checked = selectedServices.includes(s.column);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => toggleService(s.column)}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                          checked
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-muted/30 text-muted-foreground hover:border-primary/40"
                        }`}
                      >
                        <span>{s.icon}</span>
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                {selectedServices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedServices([])}
                    className="text-xs text-muted-foreground hover:text-foreground underline mt-1"
                  >
                    Réinitialiser les services
                  </button>
                )}
              </div>

              {/* Filtres modes de paiement */}
              <div className="space-y-2">
                <Label>Modes de paiement acceptés</Label>
                <p className="text-xs text-muted-foreground">Affiche les pros acceptant au moins l'un des modes cochés.</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {[
                    { id: "stripe" as const, label: "💳 Paiement sécurisé" },
                    { id: "cash" as const, label: "💶 Espèces" },
                    { id: "paypal" as const, label: "🅿️ PayPal" },
                  ].map((m) => {
                    const checked = paymentFilters.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() =>
                          setPaymentFilters((prev) =>
                            prev.includes(m.id) ? prev.filter((x) => x !== m.id) : [...prev, m.id],
                          )
                        }
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
                          checked
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border bg-muted/30 text-muted-foreground hover:border-primary/40"
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Results */}
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <User className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground">Aucun professionnel trouvé</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Essayez d'élargir vos critères de recherche</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((pro) => (
              <Card
                key={pro.id}
                className="card-neon shadow-neon hover:shadow-lg transition-all overflow-hidden cursor-pointer relative"
                onClick={() => { setSelectedProfile(pro); setQuickViewOpen(true); }}
              >
                {/* Favorite heart */}
                {role === "parent" && (
                  <button
                    type="button"
                    className={`absolute top-3 right-3 z-10 w-11 h-11 rounded-full border shadow-sm backdrop-blur flex items-center justify-center transition-all hover:scale-105 ${isFavorite(pro.user_id) ? "bg-destructive/10 border-destructive/30" : "bg-background/90 border-border hover:border-destructive/30"}`}
                    onClick={(e) => { e.stopPropagation(); void handleFavoriteToggle(pro.user_id); }}
                    aria-label={isFavorite(pro.user_id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                    title={isFavorite(pro.user_id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                  >
                    <Heart className={`w-6 h-6 transition-colors ${isFavorite(pro.user_id) ? "text-destructive fill-destructive" : "text-muted-foreground hover:text-destructive"}`} />
                  </button>
                )}
                {/* Live availability indicator dot */}
                <div className={`absolute top-4 ${role === "parent" ? "right-16" : "right-4"} w-3 h-3 rounded-full ${
                  pro.is_available
                    ? "bg-accent shadow-[0_0_8px_2px_hsl(var(--accent)/0.4)]"
                    : "bg-muted-foreground/30"
                }`}>
                  {pro.is_available && (
                    <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-50" />
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                      {pro.avatar_url ? (
                        <img src={pro.avatar_url} alt={pro.full_name || "Professionnel Click&Gard"} className="w-14 h-14 rounded-2xl object-cover" />
                      ) : (
                        <User className="w-7 h-7 text-primary" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground truncate flex items-center gap-1">
                        <Link
                          to={`/profil/${pro.user_id}`}
                          className="hover:underline hover:text-primary transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {pro.full_name || "Professionnel Click&Gard"}
                        </Link>
                        {(pro as any).identity_verified && (
                          <span title="Identité vérifiée"><ShieldCheck className="w-4 h-4 text-primary shrink-0" /></span>
                        )}
                      </h3>
                      {(pro as any).pro_category && (
                        <p className="text-xs text-primary font-medium capitalize">
                          {(pro as any).pro_category === "assistante_maternelle" ? "Assistante maternelle" : (pro as any).pro_category}
                        </p>
                      )}
                      {pro.city && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {pro.city || "Localisation à compléter"}{pro.postal_code ? ` (${pro.postal_code})` : ""}
                        </p>
                      )}
                    </div>
                  </div>

                  {pro.bio && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{pro.bio}</p>
                  )}

                  {reviewStats[pro.user_id] && (
                    <div className="flex items-center gap-1.5 mb-3">
                      <div className="flex items-center">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star
                            key={i}
                            className={`w-3.5 h-3.5 ${
                              i < Math.round(reviewStats[pro.user_id].avg)
                                ? "text-primary fill-primary"
                                : "text-muted-foreground/30"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-medium text-foreground">{reviewStats[pro.user_id].avg}</span>
                      <span className="text-xs text-muted-foreground">({reviewStats[pro.user_id].count} avis)</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    <Badge variant="secondary" className="rounded-full font-semibold">
                      {pro.hourly_rate ? `${pro.hourly_rate ? `${pro.hourly_rate} €/h` : "Tarif à compléter"}` : "Tarif non renseigné"}
                    </Badge>
                    {(() => {
                      const ia = (pro as any).instant_available as boolean | null;
                      const until = (pro as any).instant_available_until as string | null;
                      const active = !!ia && !!until && new Date(until).getTime() > Date.now();
                      return active ? (
                        <Badge className="rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 gap-1 animate-pulse">
                          <Zap className="w-3 h-3" /> Dispo dans l'heure
                        </Badge>
                      ) : null;
                    })()}
                    {pro.is_available ? (
                      <Badge className="rounded-full border availability-badge-available">
                        <Clock className="w-3 h-3 mr-1" /> Disponible
                      </Badge>
                    ) : (
                      <Badge className="rounded-full border availability-badge-unavailable">
                        Indisponible
                      </Badge>
                    )}
                    {pro.experience && (
                      <Badge variant="outline" className="rounded-full">
                        {pro.experience}
                      </Badge>
                    )}
                    {(pro as any).care_at_pro_home && (
                      <Badge variant="outline" className="rounded-full text-xs">
                        <Home className="w-3 h-3 mr-1" /> Chez le pro
                      </Badge>
                    )}
                    {(pro as any).care_at_parent_home && (
                      <Badge variant="outline" className="rounded-full text-xs">
                        <Home className="w-3 h-3 mr-1" /> À domicile
                      </Badge>
                    )}
                    {(pro as any).care_outdoor && (
                      <Badge variant="outline" className="rounded-full text-xs">
                        <TreePine className="w-3 h-3 mr-1" /> Extérieur
                      </Badge>
                    )}
                    {((pro as any).accepts_stripe ?? true) && (
                      <Badge variant="outline" className="rounded-full text-xs" title="Paiement sécurisé Click&Gard">💳 Sécurisé</Badge>
                    )}
                    {(pro as any).accepts_cash && (
                      <Badge variant="outline" className="rounded-full text-xs" title="Paiement en espèces">💶 Espèces</Badge>
                    )}
                    {(pro as any).accepts_paypal && (
                      <Badge variant="outline" className="rounded-full text-xs" title="Paiement PayPal">🅿️ PayPal</Badge>
                    )}
                  </div>


                  <div className="flex gap-2">
                    <Button className="flex-1 rounded-full bg-gradient-to-r from-primary to-secondary shadow-neon hover:opacity-90" size="sm">
                      Voir le profil
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/booking?pro=${pro.user_id}`);
                      }}
                    >
                      <Calendar className="w-4 h-4 mr-1" /> Réserver
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
          </TabsContent>

          <TabsContent value="parents" className="mt-6">
            <div className="w-full px-4 md:px-6 lg:px-8 py-8">
              {role === "pro" && !proCanViewAnnouncements ? (
                <div className="max-w-xl mx-auto">
                  <Card className="border-2 border-primary/25 shadow-neon overflow-hidden">
                    <CardContent className="p-8 text-center space-y-5">
                      <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <Lock className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-extrabold text-foreground">Annonces réservées aux PRO abonnés</h2>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                          Activez votre abonnement professionnel pour consulter les annonces des parents et pouvoir y répondre.
                        </p>
                      </div>
                      <Button
                        size="lg"
                        className="w-full rounded-full font-bold"
                        onClick={() => navigate("/subscription")}
                      >
                        <Crown className="w-5 h-5 mr-2" />
                        Activer mon abonnement PRO
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <>
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div>
                  <h2 className="font-display text-xl font-bold text-foreground">Parents / Annonces</h2>
                  <p className="text-sm text-muted-foreground">{requests.length} annonce{requests.length !== 1 ? "s" : ""} publiée{requests.length !== 1 ? "s" : ""}</p>
                </div>
                <Button variant="outline" className="rounded-full" onClick={() => navigate("/demandes")}>Voir toutes les annonces</Button>
              </div>

              {requestsLoading ? (
                <div className="text-center py-16 text-muted-foreground">Chargement...</div>
              ) : requests.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">Aucune annonce publiée pour le moment.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {requests.map((req) => {
                    const places = [
                      req.care_at_parent_home ? "À domicile" : null,
                      req.care_at_pro_home ? "Chez le pro" : null,
                      req.care_outdoor ? "Extérieur" : null,
                    ].filter(Boolean);
                    const criteria = [
                      req.non_smoker ? "Non-fumeur" : null,
                      req.has_car ? "Véhiculé(e)" : null,
                      req.first_aid_required ? "Premiers secours" : null,
                      req.pets ? "Animaux" : null,
                    ].filter(Boolean);
                    return (
                      <Card key={req.id} className="card-neon shadow-neon hover:shadow-lg transition-all overflow-hidden">
                        <CardContent className="p-6 space-y-4">
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-semibold text-foreground line-clamp-2 break-words">{formatTitleWithDepartment(req.title, req.city, (req as any).postal_code_prefix, (req as any).postal_code)}</h3>
                              {req.care_type && <Badge variant="secondary" className="rounded-full text-xs shrink-0">{CARE_TYPE_LABELS[req.care_type] ?? req.care_type}</Badge>}
                            </div>
                            {req.description && <p className="text-sm text-muted-foreground line-clamp-3">{req.description}</p>}
                          </div>

                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="rounded-full max-w-full whitespace-normal break-words"><MapPin className="w-3 h-3 mr-1 shrink-0" />{formatCityWithDepartment(req.city, (req as any).postal_code_prefix, (req as any).postal_code)}</Badge>
                            {req.proposed_rate && <Badge variant="outline" className="rounded-full">{req.proposed_rate} €/h</Badge>}
                            {req.start_date && <Badge variant="outline" className="rounded-full">{new Date(req.start_date).toLocaleDateString("fr-FR")}{req.end_date ? ` → ${new Date(req.end_date).toLocaleDateString("fr-FR")}` : ""}</Badge>}
                            {req.start_time && req.end_time && <Badge variant="outline" className="rounded-full">{req.start_time.slice(0, 5)} - {req.end_time.slice(0, 5)}</Badge>}
                          </div>

                          {places.length > 0 && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Lieu :</span> {places.join(" • ")}</p>}
                          {criteria.length > 0 && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Critères :</span> {criteria.join(" • ")}</p>}
                          {req.languages && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Langues :</span> {req.languages}</p>}
                          {req.additional_tasks && <p className="text-xs text-muted-foreground line-clamp-2"><span className="font-medium text-foreground">Tâches :</span> {req.additional_tasks}</p>}

                          <div className="flex gap-2 pt-1">
                            <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={() => navigate(`/demandes?request=${req.id}`)}>
                              Voir l'annonce
                            </Button>
                            {role === "pro" && (
                              <Button size="sm" className="flex-1 rounded-full" onClick={() => navigate(`/demandes?request=${req.id}`)}>
                                Répondre
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ProfileQuickView
        profile={selectedProfile}
        open={quickViewOpen}
        onOpenChange={setQuickViewOpen}
        onMessage={role === "parent" ? async () => {
          if (!user || !selectedProfile) {
            navigate("/auth");
            return;
          }
          const { data: existing, error: lookupError } = await supabase
            .from("conversations")
            .select("id")
            .eq("parent_id", user.id)
            .eq("pro_id", selectedProfile.user_id)
            .maybeSingle();
          if (lookupError) {
            console.error("Conversation lookup failed:", lookupError);
            return;
          }
          if (existing?.id) {
            navigate(`/messages?convo=${existing.id}`);
            return;
          }
          const { data: created, error: createError } = await supabase
            .from("conversations")
            .insert({ parent_id: user.id, pro_id: selectedProfile.user_id })
            .select("id")
            .single();
          if (createError) {
            console.error("Conversation creation failed:", createError);
            return;
          }
          navigate(`/messages?convo=${created.id}`);
        } : undefined}
      />
    </div>
  );
};

export default SearchPage;
